import React, { useEffect, useState } from "react"
import { Backdrop, CircularProgress, Stack, Typography } from "@mui/material"
import { GallerySection } from "@/shared/types/product"
import { useAuth } from "@/features/auth/context/AuthContext"
import { usePlan } from "@/shared/hooks/usePlan"
import UpgradeDialog from "@/features/billing/components/UpgradeDialog"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import GenerateWithAIFab from "@/shared/components/generate-with-ai/GenerateWithAIFab"
import { useGenerateWithAIAction } from "@/shared/hooks/useGenerateWithAIAction"

export default function GenerateWithAI({ sections, onReset }: { sections: GallerySection[]; onReset: () => void }) {
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const { showSnackbar } = useSnackbar()
  const { user } = useAuth()
  const { canGenerateImage } = usePlan()
  const { loading, run } = useGenerateWithAIAction({
    userId: user?.uid ?? null,
    notify: showSnackbar,
    sectionsFactory: () => sections,
    canGenerateImage,
    onRequireUpgrade: () => setUpgradeOpen(true),
    onSuccess: () => {
      onReset()
    },
    successMessage: "Products saved successfully!",
    failureMessage: "Failed to upload images. Please try again."
  })

  useEffect(() => {
    if (!loading) return
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ""
    }
    window.addEventListener("beforeunload", handleBeforeUnload)
    return () => window.removeEventListener("beforeunload", handleBeforeUnload)
  }, [loading])

  const hasAllPromptsSelected = sections.length > 0 && sections.every(s => s.selectedPromptIds.length > 0)

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: theme => theme.zIndex.modal + 1, color: "#fff" }}>
        <Stack alignItems="center" gap={2} bgcolor="white" color="black" p={4} borderRadius={2}>
          <CircularProgress color="inherit" size={56} />
          <Typography variant="h6" fontWeight="bold">
            Uploading your images…
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ maxWidth: 340 }}>
            Please do not close this page or navigate away. Leaving now will cancel your image generation.
          </Typography>
        </Stack>
      </Backdrop>

      <UpgradeDialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason="You've used all your AI image credits. Buy more credits to continue generating images."
      />

      <GenerateWithAIFab
        onClick={run}
        disabled={sections.length === 0 || !hasAllPromptsSelected}
        loading={loading}
        tooltip={!hasAllPromptsSelected ? "Select at least one prompt for each product" : "Generate With AI"}
      />
    </>
  )
}
